/**
 * Admin shipping-method write contract — what an operator posts to
 * `POST /api/admin/users/[id]/shipping-methods` (create) and
 * `PUT /api/admin/users/[id]/shipping-methods/[methodId]` (update) to manage a
 * *target seller's* `shipping_methods` rows from the admin listings editor.
 *
 * Why this route exists at all: the `shipping_methods` RLS policies are
 * `user_id = auth.uid()` for INSERT/UPDATE/DELETE (only public SELECT is open).
 * So an admin editing another user's listing cannot write that user's methods
 * with the browser client — the write would silently affect 0 rows. These
 * routes run `supabaseAdmin` (service-role) behind `requireAdmin()`, scoping
 * every write to the `[id]` in the path so an admin can manage exactly the
 * viewed seller's methods.
 *
 * NOT mobile-consumed — admin-gated, so the schema can tighten freely.
 *
 * Field shapes mirror the `shipping_methods` table writable columns:
 *   - `price` is TEXT in the DB (`price text`), so the wire carries a string,
 *     matching the existing browser-client insert/update in `EditShippingTab`.
 *   - `currency` is `varchar(3)` (ISO-4217).
 *   - `bannedCountries` → `banned_countries text[]` (ISO-3166 alpha-2 deny-list).
 *
 * @see app/api/admin/users/[id]/shipping-methods/route.ts — POST handler
 * @see app/api/admin/users/[id]/shipping-methods/[methodId]/route.ts — PUT/DELETE
 * @see lib/admin/shipping-methods.ts — client adapter (builders)
 * @see components/my-listings/EditShippingTab.tsx — consumer (admin branch)
 */

import { z } from "zod";
import { pinned } from "./pin";

/** Common writable fields shared by create + update. */
export interface AdminShippingMethodWrite {
  name: string;
  description: string;
  /** Stored as TEXT in `shipping_methods.price` — wire carries a string. */
  price: string;
  /** ISO-3166 alpha-2 deny-list → `banned_countries text[]`. */
  bannedCountries: string[];
  /** ISO-4217 (e.g. "USD") → `currency varchar(3)`. */
  currency: string;
}

/**
 * Create body. `id` is client-generated (the browser editor mints a `uuidv4`
 * before insert so optimistic UI state and the DB row share the same id) — the
 * route writes it verbatim under the path `[id]` user.
 */
export interface AdminShippingMethodCreate extends AdminShippingMethodWrite {
  id: string;
}

/** Update body — same writable fields, method id comes from the path. */
export type AdminShippingMethodUpdate = AdminShippingMethodWrite;

/** 2xx response for create/update/delete. */
export interface AdminShippingMethodMutationResponse {
  success: boolean;
}

const writeFields = {
  name: z.string(),
  description: z.string(),
  price: z.string(),
  bannedCountries: z.array(z.string()),
  currency: z.string().min(1).max(3),
};

export const AdminShippingMethodCreateSchema =
  pinned<AdminShippingMethodCreate>()(
    z.object({
      id: z.string().min(1),
      ...writeFields,
    }),
  );

export const AdminShippingMethodUpdateSchema =
  pinned<AdminShippingMethodUpdate>()(z.object({ ...writeFields }));

export const AdminShippingMethodMutationResponseSchema =
  pinned<AdminShippingMethodMutationResponse>()(
    z.object({ success: z.boolean() }),
  );
